import React, { useMemo } from 'react';
import { catalogueStats } from 'app/data/datasets';
import { DatasetList } from './DatasetList';
import { PlanColumn, PlanComparisonTable, PlanRow } from './ModernPlanBox';

/**
 * Prices in dollars per month. Single source for the comparison table and for
 * the per-dataset figure in DatasetList, so the two cannot disagree.
 */
const PROFESSIONAL_PRICE = 89;
const CORPORATE_PRICE = 295;

/** Both plans point at the same signup entry point. */
const SIGNUP_HREF = 'https://sov.ai/home';

/**
 * Column order is also cell order in every row below — `PlanRow.cells[i]`
 * belongs to `PLANS[i]`.
 */
const PLANS: PlanColumn[] = [
  {
    title: 'Professional',
    price: PROFESSIONAL_PRICE,
    ctaText: 'Start Professional Plan',
    href: SIGNUP_HREF,
  },
  {
    title: 'Corporate',
    price: CORPORATE_PRICE,
    ctaText: 'Get Corporate Access',
    href: SIGNUP_HREF,
    mostPopular: true,
  },
];

/**
 * The same five features the two cards listed, turned into rows so both values
 * for an attribute sit on one line. Nothing was added or dropped.
 */
const planRows = (datasetCount: number): PlanRow[] => [
  // No `price` row: the column headers already carry $89 and $295, and
  // repeating them here would be two places to change one number.
  {
    label: 'seats',
    cells: [
      { value: '1', numeric: true, note: "that's you" },
      { value: '5', numeric: true, note: 'different IPs' },
    ],
  },
  {
    label: 'queries',
    cells: [{ value: 'unlimited' }, { value: 'unlimited' }],
  },
  {
    label: 'datasets',
    cells: [
      { value: `all ${datasetCount}`, numeric: true },
      { value: `all ${datasetCount}`, numeric: true },
    ],
  },
  {
    label: 'commercial use',
    cells: [{ value: true }, { value: true }],
  },
  {
    label: 'dedicated support',
    cells: [{ value: false }, { value: true }],
  },
  {
    label: 'billing',
    cells: [{ value: 'monthly' }, { value: 'monthly' }],
  },
];

const TERMS = [
  '14-day free trial on every plan.',
  '30-day money-back guarantee.',
];

export const PricingPlansSection = () => {
  return (
    <section
      id="pricing"
      aria-labelledby="pricing-heading"
      className="w-full bg-[#0d1117] px-4 py-16 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-3xl text-center">
        <h2
          id="pricing-heading"
          className="font-mono text-2xl font-semibold text-white md:text-3xl"
        >
          Pricing
        </h2>
        <p className="mt-4 text-base leading-relaxed text-gray-200">
          Sov.ai data subscriptions are{' '}
          <span className="font-semibold text-white">
            discontinued until further notice
          </span>
          .
        </p>
        <p className="mt-3 text-sm leading-relaxed text-gray-400">
          The platform is not currently offered on a paid or commercial basis,
          and no new subscriptions are being taken.
        </p>
      </div>
    </section>
  );
};
